import { useState } from 'react';
import { Button, Stack, Tooltip } from '@mui/material';
import {
  cancelPublication,
  exchangeLabel,
  retryPublication,
  withdrawPublication,
  STATUS,
  type Publication,
} from './publicationsApi';
import { toErrorMessage } from '../userOffers/orderActionsApi';

/**
 * Przyciski przy jednym wierszu publikacji — te same w sekcji zlecenia
 * i w kolejce. Które widać, zależy od statusu, tak jak odmawia serwer:
 * anulować można oczekującą, ponowić zakończoną błędem, wycofać wystawioną.
 *
 * Wycofanie z wymuszeniem pojawia się dopiero po odmowie giełdy — to
 * świadoma decyzja człowieka, że oferta ma być uznana za zdjętą mimo błędu,
 * i nie może być pierwszym przyciskiem pod ręką.
 */
export const PublicationActions = ({
  publication: publikacja,
  onChanged: poZmianie,
  notify: powiadom,
}: {
  publication: Publication;
  onChanged: () => void | Promise<void>;
  notify: (asText: string, kind: 'success' | 'error' | 'warning') => void;
}) => {
  const [busy, setBusy] = useState(false);
  const [exchangeRefusal, setExchangeRefusal] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, successMessage: string) => {
    setBusy(true);
    try {
      await action();
      powiadom(successMessage, 'success');
      await poZmianie();
      return true;
    } catch (e) {
      powiadom(toErrorMessage(e), 'error');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // „TIMOCOM nr 2” zamiast kodu „timocom #2” (01.10.2026).
  const label = `${exchangeLabel(publikacja.exchange)} nr ${publikacja.postingNumber}`;

  if (publikacja.status === STATUS.PENDING) {
    return (
      <Button
        size="small"
        color="warning"
        disabled={busy}
        onClick={() => void run(() => cancelPublication(publikacja.id), `Anulowano publikację ${label}.`)}
      >
        Anuluj
      </Button>
    );
  }

  if (publikacja.status === STATUS.FAILED) {
    return (
      <Button
        size="small"
        disabled={busy}
        onClick={() => void run(() => retryPublication(publikacja.id), `Ponowiono publikację ${label}.`)}
        data-testid="trd-publikacja-ponow"
      >
        Ponów
      </Button>
    );
  }

  if (publikacja.status === STATUS.POSTED) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Button
          size="small"
          color="error"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              try {
                await withdrawPublication(publikacja.id);
                setExchangeRefusal(null);
              } catch (e) {
                setExchangeRefusal(toErrorMessage(e));
                throw e;
              }
            }, `Wycofano ofertę ${label} z giełdy.`)
          }
        >
          Wycofaj
        </Button>
        {/* describeChild: podpowiedź OPISUJE przycisk, nie zastępuje jego nazwy —
            inaczej MUI wstawiłby treść podpowiedzi jako aria-label. Odmowę
            giełdy pokazało już powiadomienie — tu sam skutek (01.10.2026). */}
        {exchangeRefusal && (
          <Tooltip
            describeChild
            title="Giełda nie potwierdziła wycofania. Ten przycisk oznaczy ofertę w systemie jako wycofaną, choć giełda tego nie potwierdziła — oferta może nadal być tam widoczna."
          >
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={busy}
              onClick={() =>
                void run(
                  () => withdrawPublication(publikacja.id, { force: true }),
                  `Oznaczono ofertę ${label} jako wycofaną mimo odmowy giełdy — sprawdź ją na giełdzie.`,
                )
              }
            >
              Wycofaj mimo błędu
            </Button>
          </Tooltip>
        )}
      </Stack>
    );
  }

  return null;
};
